import React, { useEffect, useRef, useState } from 'react';
import { MotionImage } from '../ui/MotionPrimitives';
import {
  InfiniteLogoStrips,
  LogoItem,
  LogoStrip,
} from '../../utils/infiniteLogoStrips';

const baseLogos: LogoItem[] = [
  {
    id: 'dubai',
    src: '/images/logoforclients/Dubai-Properties-Logo.png',
    alt: 'Dubai Properties',
  },
  {
    id: 'emaar',
    src: '/images/logoforclients/EMAAR.png',
    alt: 'Emaar',
  },
  {
    id: 'imtaiz',
    src: '/images/logoforclients/imtaiz developments.png',
    alt: 'Imtaiz Developments',
  },
  {
    id: 'meraas',
    src: '/images/logoforclients/meraas.png',
    alt: 'Meraas',
  },
  {
    id: 'hh',
    src: '/images/logoforclients/e7e14173890a725c0deb84e668642e39.webp',
    alt: 'Client Logo',
  },
];

const STRIP_WIDTH_PX = 900; // approximate width of one logo strip (tune to match visual width)
const SPEED_PX_PER_SECOND = 40;

const ClientLogoStrip: React.FC = () => {
  const [strips, setStrips] = useState<LogoStrip[]>([]);
  const engineRef = useRef<InfiniteLogoStrips | null>(null);
  const lastTimeRef = useRef<number | null>(null);

  useEffect(() => {
    const viewportWidth = window.innerWidth || 1280;

    engineRef.current = new InfiniteLogoStrips({
      baseLogos,
      stripWidth: STRIP_WIDTH_PX,
      viewportWidth,
      speedPxPerSecond: SPEED_PX_PER_SECOND,
    });
    setStrips(engineRef.current.strips);

    let frameId: number;

    const tick = (time: number) => {
      if (!engineRef.current) return;
      if (lastTimeRef.current == null) {
        lastTimeRef.current = time;
      }
      const delta = time - lastTimeRef.current;
      lastTimeRef.current = time;

      engineRef.current.update(delta);
      setStrips(engineRef.current.strips);

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);

    const handleResize = () => {
      const width = window.innerWidth || viewportWidth;
      engineRef.current = new InfiniteLogoStrips({
        baseLogos,
        stripWidth: STRIP_WIDTH_PX,
        viewportWidth: width,
        speedPxPerSecond: SPEED_PX_PER_SECOND,
      });
      setStrips(engineRef.current.strips);
      lastTimeRef.current = null;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      engineRef.current = null;
    };
  }, []);

  return (
    <div className="relative w-full bg-white pt-10 pb-4 md:pt-16 md:pb-6 overflow-hidden">
      {strips.map((strip) => (
        <div
          key={strip.id}
          className="absolute inset-y-0 flex items-center gap-10 md:gap-16"
          style={{
            transform: `translateX(${strip.x}px)`,
          }}
        >
          {strip.logos.map((logo) => (
            <MotionImage
              key={logo.id}
              src={logo.src}
              alt={logo.alt}
              className="h-10 md:h-12 w-auto object-contain"
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export default ClientLogoStrip;
